import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Coins,
  Users,
  Globe,
  TrendingUp,
  Shield,
  Heart,
  Sparkles,
  ExternalLink,
  Music,
  Gift,
  ArrowRight,
  Play,
  QrCode
} from 'lucide-react';
import QRCodeShare from '@/components/QRCodeShare';

// Social Icons
const InstagramIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function LinkInBio() {
  const [clickedLink, setClickedLink] = useState(null);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const handleLinkClick = (linkId, url) => {
    setClickedLink(linkId);
    setTimeout(() => setClickedLink(null), 300);
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  const mainLinks = [
    {
      id: 'join',
      title: '🚀 Join Susu Money FREE',
      subtitle: 'Start saving with $0.10',
      url: '/login',
      gradient: 'from-indigo-600 to-purple-600',
      featured: true
    },
    {
      id: 'clubs',
      title: '👥 Browse Savings Clubs',
      subtitle: 'Find your community',
      url: '/clubs',
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      id: 'video',
      title: '🎬 Watch: What is Susu?',
      subtitle: 'Learn about community savings',
      url: 'https://youtube.com/shorts/GpUOTDDD2nY',
      gradient: 'from-red-500 to-pink-600'
    },
    {
      id: 'advertise',
      title: '📢 Advertise With Us',
      subtitle: 'Reach 5,000+ savers',
      url: '/advertise',
      gradient: 'from-amber-500 to-orange-600'
    },
    {
      id: 'ads',
      title: '📱 Share Our Ads',
      subtitle: 'Help spread the word',
      url: '/ads',
      gradient: 'from-cyan-500 to-blue-600'
    }
  ];

  const socialLinks = [
    { id: 'instagram', icon: InstagramIcon, label: 'Instagram', url: 'https://instagram.com/globalsusumoneys', color: 'hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]' },
    { id: 'tiktok', icon: TikTokIcon, label: 'TikTok', url: 'https://tiktok.com/@susumoney', color: 'hover:bg-black' },
    { id: 'facebook', icon: FacebookIcon, label: 'Facebook', url: 'https://facebook.com/susumoney', color: 'hover:bg-[#1877F2]' },
    { id: 'twitter', icon: XIcon, label: 'X', url: 'https://x.com/susumoney', color: 'hover:bg-black' },
    { id: 'youtube', icon: YouTubeIcon, label: 'YouTube', url: 'https://youtube.com/@susumoney', color: 'hover:bg-red-600' },
    { id: 'whatsapp', icon: WhatsAppIcon, label: 'WhatsApp', url: 'https://wa.me/message/susumoney', color: 'hover:bg-[#25D366]' }
  ];

  const stats = [
    { value: '5,000+', label: 'Savers' },
    { value: '50+', label: 'Countries' },
    { value: '$500K+', label: 'Saved' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-900 via-purple-900 to-pink-900">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/10 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-md mx-auto px-4 py-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Profile Picture */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white/20"
          >
            <Coins className="w-12 h-12 text-white" />
          </motion.div>

          {/* Name & Verification */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-2xl font-bold text-white">Susu Money</h1>
            <Badge className="bg-blue-500 text-white text-xs px-1.5">
              <Sparkles className="w-3 h-3" />
            </Badge>
          </div>

          {/* Bio */}
          <p className="text-white/80 text-sm mb-4 max-w-xs mx-auto">
            🌍 Global Community Savings Platform<br/>
            💰 Save together, grow together<br/>
            ✨ Start with just $0.10
          </p>

          {/* Stats */}
          <div className="flex justify-center gap-6 mb-4">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-white font-bold text-lg">{stat.value}</div>
                <div className="text-white/60 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Main Links */}
        <div className="space-y-3 mb-8">
          {mainLinks.map((link, index) => (
            <motion.button
              key={link.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleLinkClick(link.id, link.url)}
              className={`w-full p-4 rounded-2xl bg-gradient-to-r ${link.gradient} text-white text-left shadow-lg hover:shadow-xl transition-all ${
                link.featured ? 'ring-2 ring-white/30' : ''
              } ${clickedLink === link.id ? 'scale-95' : ''}`}
              data-testid={`link-${link.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{link.title}</div>
                  <div className="text-white/80 text-sm">{link.subtitle}</div>
                </div>
                <ArrowRight className="w-5 h-5 opacity-70" />
              </div>
              {link.featured && (
                <div className="mt-2 flex items-center text-xs text-white/70">
                  <Heart className="w-3 h-3 mr-1" />
                  Most popular
                </div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <p className="text-white/60 text-center text-sm mb-4">Follow us everywhere</p>
          <div className="flex justify-center gap-3">
            {socialLinks.map((social) => (
              <motion.button
                key={social.id}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(social.url, '_blank')}
                className={`w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all ${social.color} hover:text-white`}
                title={social.label}
                data-testid={`social-${social.id}`}
              >
                <social.icon />
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-3 gap-3 mb-8"
        >
          {[
            { icon: Shield, label: '100% Secure' },
            { icon: Globe, label: 'Worldwide' },
            { icon: TrendingUp, label: 'Earn Interest' }
          ].map((feature, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <feature.icon className="w-6 h-6 text-white mx-auto mb-1" />
              <p className="text-white/80 text-xs">{feature.label}</p>
            </div>
          ))}
        </motion.div>

        {/* QR Code Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="mb-8"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
            <div className="flex items-center justify-center mb-3">
              <QrCode className="w-5 h-5 text-white mr-2" />
              <span className="text-white font-medium">Scan to Join</span>
            </div>
            <QRCodeShare variant="inline" size="sm" showDownload={true} />
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <p className="text-white/40 text-xs mb-2">
            🎵 Powered by African Music 24/7
          </p>
          <div className="flex items-center justify-center gap-2 text-white/60 text-xs">
            <Coins className="w-4 h-4" />
            <span>© 2025 Susu Money</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
